import React, { useEffect, useState } from 'react'
import { toast } from 'sonner'

import { InProductFeedback } from 'components/feedback/InProductFeedback'
import {
  useGetFeedbackPromptsNext,
  usePostFeedbackPromptsIdDismiss,
  usePostFeedbackSubmissions,
} from 'utils/web_api'

type FeedbackRating = 'great' | 'okay' | 'not_good'

export default function FeedbackPromptContainer (): React.JSX.Element | null {
  const { data: prompt } = useGetFeedbackPromptsNext()
  const dismissMutation = usePostFeedbackPromptsIdDismiss()
  const submitFeedbackMutation = usePostFeedbackSubmissions()
  const [hidden, setHidden] = useState(true)
  const [impressionTracked, setImpressionTracked] = useState(false)
  const [step, setStep] = useState(0)
  const [rating, setRating] = useState<FeedbackRating | null>(null)

  useEffect(() => {
    if (prompt?.id && !impressionTracked) {
      window.segment?.track('Feedback widget viewed', {
        prompt_key: prompt.prompt_key,
      })
      setImpressionTracked(true)
      setHidden(false)
    }
  }, [prompt, impressionTracked, hidden])

  if (!prompt?.id) return null

  const handleDismiss = () => {
    if (prompt.id) {
      dismissMutation.mutateAsync({ id: prompt.id })
      window.segment?.track('Feedback widget dismissed', {
        prompt_key: prompt.prompt_key,
        step
      })
    }
    setHidden(true)
  }

  const getTitle = () => {
    switch (step) {
      case 0:
        return 'How did that go?'
      default:
        return 'Thanks!'
    }
  }

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <InProductFeedback.Rating
            name='rating'
            onSubmit={async (value) => {
              const ratingValue = value as FeedbackRating
              setRating(ratingValue)

              try {
                await submitFeedbackMutation.mutateAsync({
                  data: {
                    rating: ratingValue,
                    feedback_prompt_id: prompt.id,
                  },
                })
                window.segment?.track('Feedback rating submitted', {
                  prompt_key: prompt.prompt_key,
                  rating: ratingValue,
                })
                setStep(1)
              } catch (error) {
                toast.error('Failed to submit feedback. Please try again.')
                window.segment?.track('Feedback submission failed', {
                  prompt_key: prompt.prompt_key,
                  rating: ratingValue,
                  error: error instanceof Error ? error.message : 'Unknown error',
                })
              }
            }}
            scale={{
              great: 'Great',
              okay: 'Okay',
              not_good: 'Not good',
            }}
          />
        )
      case 1:
        return (
          <InProductFeedback.OpenEnded
            buttonText='Send feedback'
            name='comment'
            onSubmit={async (value) => {
              if (value) {
                try {
                  await submitFeedbackMutation.mutateAsync({
                    data: {
                      rating: rating as FeedbackRating,
                      comment: value,
                      feedback_prompt_id: prompt.id,
                    },
                  })
                  window.segment?.track('Feedback comment submitted', {
                    prompt_key: prompt.prompt_key,
                    rating,
                    comment: value,
                  })
                } catch (error) {
                  toast.error('Failed to submit comment. Please try again.')
                  window.segment?.track('Feedback submission failed', {
                    prompt_key: prompt.prompt_key,
                    rating,
                    comment: value,
                    error: error instanceof Error ? error.message : 'Unknown error',
                  })
                  return
                }
              }
              setStep(2)
            }}
            placeholder="Anything else you'd like to share?"
          />
        )
      case 2:
        return (
          <InProductFeedback.Confirmation
            message='We got your response 💌'
            subText={
              <>
                If you need any other help, you can reach out to us at{' '}
                <a href='mailto:support@thatch.com'>support@thatch.com</a>.
              </>
            }
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='px-2 mb-2'>
      <InProductFeedback
        open={!hidden}
        onOpenChange={(open) => setHidden(!open)}
        onDismiss={handleDismiss}
        title={getTitle()}
      >
        {renderStep()}
      </InProductFeedback>
    </div>
  )
}
